/* eslint-disable react/jsx-props-no-spreading */
import React, { ChangeEvent, FunctionComponent, SyntheticEvent, useCallback, useContext, useEffect, useState } from 'react';

// MUI
import Stack from '@mui/material/Stack';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

// OL
import OlBaseLayer from 'ol/layer/Base';
import OlLayerGroup from 'ol/layer/Group';
import OlView from 'ol/View';
import { EventsKey } from 'ol/events';
import BaseEvent from 'ol/events/Event';

// Custom components

import { IconButton } from '@mui/material';
import LayerTreeItem from './LayerTreeItem';
import MapContext, { MapContextType } from '@/context/MapContext/MapContext';
import { flattenLayers, checkIfLayerVisible } from '@/lib/olHelpers';

// Types
import { ILayerTreeProps } from '@/@types/components/Map/Controls/Custom/LayerTree';
import LayerTreeActions from './LayerTreeActions';

const ExpandIcon = () => (
  <IconButton size="small">
    <ChevronRightIcon />
  </IconButton>
);
const CollapseIcon = () => (
  <IconButton size="small">
    <ExpandMoreIcon />
  </IconButton>
);

const LayerTree: FunctionComponent<ILayerTreeProps> = (props) => {
  const [expanded, setExpanded] = useState<string[] | null>(null);
  const [eventKey, setEventKey] = useState<EventsKey | null>(null);
  const [resetKey, setResetKey] = useState(0);

  // Dont remove this! Without this, Layer Picker doesnt rerender on Map zoom
  const [, setCurrentZoomLevel] = useState<number | undefined>(8);

  const mapContext = useContext(MapContext) as MapContextType;

  const { mapId, layersCollection, onLayerVisibilityChange, allowSwiperSlider, setSwipeValue, swipeState, setLayerSwipeState } = props;

  let mounted = false;

  const selected: string[] = [];

  const handleToggle = (evt: SyntheticEvent | null, nodeIds: string[]): void => {
    setExpanded(nodeIds);
  };

  const handleSelect = useCallback(
    (evt: SyntheticEvent | ChangeEvent<{}> | null, nodeIds: string[] | string): void => {
      if (!evt) return;

      evt.preventDefault(); // prevent onNodeToggle

      if (!Array.isArray(nodeIds)) {
        const layer = flattenLayers(layersCollection.getArray(), 5).find((x) => x.get('id') === nodeIds);

        if (layer) {
          const layerVisible = checkIfLayerVisible(mapContext, layer);

          const isGroup = layer instanceof OlLayerGroup;
          if (isGroup) {
            const childLayers = flattenLayers(layer.getLayers().getArray() as OlBaseLayer[], 5);

            const childsVisible = childLayers.filter((x: OlBaseLayer) => checkIfLayerVisible(mapContext, x) && !(x instanceof OlLayerGroup));
            const childsHidden = childLayers.filter((x: OlBaseLayer) => !checkIfLayerVisible(mapContext, x) && !(x instanceof OlLayerGroup));

            const groupIndeterminate = childsVisible.length > 0 && childsHidden.length > 0;

            const newVisible = groupIndeterminate ? true : childsVisible.length === 0;

            if (newVisible) {
              childsHidden.forEach((l) => {
                const lid = l.get('id');
                mapContext.setLayerVisibility(lid, true);
              });
            } else {
              childsVisible.forEach((l) => {
                const lid = l.get('id');
                l.setVisible(false);
                mapContext.setLayerVisibility(lid, false);
              });
            }
          } else {
            mapContext.setLayerVisibility(nodeIds, !layerVisible);
          }

          onLayerVisibilityChange(layer, !layerVisible);
        }
      }
    },
    [layersCollection, onLayerVisibilityChange, mapContext?.layerVisibility]
  );

  const layers = layersCollection.getArray();



  useEffect(() => {
    mounted = true;
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (expanded === null) {
      try {
        const layerPickerState = localStorage.getItem('layer_pickers');
        let stateObject: Record<number, string[]> = layerPickerState ? JSON.parse(layerPickerState) : {};

        if (typeof stateObject !== 'object' || Array.isArray(stateObject)) {
          stateObject = {};
        }

        if (stateObject[mapId]) {
          if (Array.isArray(stateObject[mapId]) && stateObject[mapId].every((item: unknown) => typeof item === 'string')) {
            setExpanded(stateObject[mapId]);
          } else {
            setExpanded([]);
          }
        } else {
          setExpanded([]);
        }
      } catch (error) {
        // console.error('Error accessing or parsing localStorage:', error);
        setExpanded([]);
      }
    }
  }, [mapId]);

  useEffect(() => {
    if (expanded !== null) {
      try {
        const layerPickerState = localStorage.getItem('layer_pickers');
        let stateObject: Record<number, string[]> = layerPickerState ? JSON.parse(layerPickerState) : {};

        if (typeof stateObject !== 'object' || Array.isArray(stateObject)) {
          stateObject = {};
        }

        stateObject[mapId] = expanded;
        localStorage.setItem('layer_pickers', JSON.stringify(stateObject));
      } catch (error) {
        // will happen when stateObject is not valid json object e.g. when user modifies it with gibberish
        localStorage.setItem('layer_pickers', JSON.stringify({ [mapId]: expanded }));
        // console.log('Error saving to localStorage:', error);
      }
    }
  }, [expanded, mapId]);

  useEffect(() => {
    if (mapContext.map) {
      const view = mapContext.map.getView();
      const evKey = view.on('change', (evt: BaseEvent) => {
        const v = evt.target as OlView;
        const newZoomLevel = v.getZoom();
        setCurrentZoomLevel(newZoomLevel);
      });

      setEventKey(evKey);
    }

    return () => {
      if (mounted) {
        if (mapContext?.map && eventKey) {
          const view = mapContext.map.getView();
          view.un('change', eventKey.listener);
        }
      }
    };
  }, [mapContext.map]);

  const handleChangeSwipeState = useCallback(
    (layerId: string, newValue: null | 'L' | 'R') => {
      if (setLayerSwipeState) {
        setLayerSwipeState((prev) => {
          const oldStatecopy = { ...prev }; // shallow copy is fine right?
          if (newValue === 'L' || newValue === 'R') {
            oldStatecopy[layerId] = newValue;
          } else {
            // null
            delete oldStatecopy[layerId];
          }
          return oldStatecopy;
        });
      }
    },
    [setLayerSwipeState]
  );

  const handleClearSwipeState = useCallback(() => {
    if (setLayerSwipeState) {
      setLayerSwipeState({});
      setResetKey(prev => prev+1);
    }
  }, [setLayerSwipeState]);

  return (
    <Stack>
      <LayerTreeActions allowSwiperSlider={allowSwiperSlider} setSwipeValue={setSwipeValue} onClearSwipeState={handleClearSwipeState} />
      <SimpleTreeView
        key={resetKey}
        multiSelect
        checkboxSelection
        slots={{ expandIcon: ExpandIcon, collapseIcon: CollapseIcon }}
        expandedItems={expanded || []}
        selectedItems={selected}
        onSelectedItemsChange={handleSelect}
        onExpandedItemsChange={handleToggle}
        sx={{
          p: 1,
          mb: 2,
        }}
      >
        {layers.map((layer) => (
          <LayerTreeItem
            key={`lti-${layer.get('id')}`}
            layer={layer}
            onNodeSelect={handleSelect}
            currentZoomLevel={mapContext.map?.getView().getZoom()}
            swipeState={swipeState}
            onChangeSwipeState={handleChangeSwipeState}
            allowSwiperSlider={allowSwiperSlider}
          />
        ))}
      </SimpleTreeView>
    </Stack>
  );
};

export default LayerTree;
